import type { UnitCode } from '~/graphql/graphql';
import { calcItemLoads, type CalcItemLoadsInput } from '~/utils/order';
import {
  hasDimensions,
  toStandardUnits,
  toStoragePriceOrNull,
  unitsPerPieceOrNull,
  type Dimensions,
} from '~/utils/unitConversion';

/**
 * Converts a buyer-entered price (per `displayUnit`) into the standard unit used
 * for storage, so the backend can convert it back for display.
 */
export function toStoragePrice(
  displayPrice: number,
  displayUnit: UnitCode,
  standardUnit: UnitCode | null | undefined,
  dims: Dimensions
): number {
  return (
    toStoragePriceOrNull(displayPrice, displayUnit, standardUnit, dims) ??
    displayPrice
  );
}

export function bundleSizeInUnit(
  piecesPerBundle: number,
  unit: UnitCode,
  dims: Dimensions
): number {
  if (!piecesPerBundle || !hasDimensions(dims)) return 0;
  const upp = unitsPerPieceOrNull(unit, dims);
  return upp == null ? 0 : piecesPerBundle * upp;
}

export type TopUpInput = {
  quantity: { value: number; unit: UnitCode };
  platformProductsLengths: CalcItemLoadsInput['platformProductsLengths'];
  vehiclePayload: { value: number; unit: UnitCode } | null | undefined;
  totalLoads: number;
  bundleSize: number;
  dims: Dimensions;
};

/**
 * The quantity that brings the cart's TOTAL loads up to the next whole vehicle
 * without overshooting it, or 0 when no whole bundle fits in the gap.
 *
 * Returns an absolute target quantity in the item's own unit, not a delta.
 */
export function computeTopUpQuantity({
  quantity,
  platformProductsLengths,
  vehiclePayload,
  totalLoads,
  bundleSize,
  dims,
}: TopUpInput): number {
  if (!vehiclePayload || vehiclePayload.value <= 0 || bundleSize <= 0) return 0;

  // Rounded to the same 2dp the loads badge shows, so float drift from unit
  // conversion (3.0002 displayed as "3.00") isn't read as a partial load.
  const displayPrecisionLoads = Math.round(totalLoads * 100) / 100;
  const targetLoadCount = Math.ceil(displayPrecisionLoads - 0.0001);

  // The gap is measured against the cart TOTAL, not this line, because
  // totalLoads sums every item — using this item's own target would overshoot
  // by the number of items in the cart.
  const gapVehicleQty =
    (targetLoadCount - displayPrecisionLoads) * vehiclePayload.value;
  const gapToTarget =
    vehiclePayload.unit === quantity.unit
      ? gapVehicleQty
      : toStandardUnits(
          gapVehicleQty,
          vehiclePayload.unit,
          quantity.unit,
          dims.lengthInches,
          dims.widthInches,
          dims.thicknessInches
        );
  if (gapToTarget <= 0) return 0;

  // +1e-9 absorbs float drift so a gap that is mathematically an exact multiple
  // of bundleSize isn't floored one bundle short.
  const maxBundles = Math.floor(gapToTarget / bundleSize + 1e-9);

  const currentItemLoads = calcItemLoads(
    { quantity, platformProductsLengths },
    vehiclePayload
  );
  const hitsWholeTruck = (bundles: number): boolean => {
    const newItemLoads = calcItemLoads(
      {
        quantity: {
          value: quantity.value + bundles * bundleSize,
          unit: quantity.unit,
        },
        platformProductsLengths,
      },
      vehiclePayload
    );
    const newTotal = displayPrecisionLoads - currentItemLoads + newItemLoads;
    return Math.round(newTotal * 100) / 100 === targetLoadCount;
  };

  // Prefer the bundle that lands exactly on a whole vehicle — when the gap is
  // smaller than one bundle that is the single allowed overshoot, and rejecting
  // it is what blocks the LVL / one-piece-per-truck cases. Otherwise get as
  // close as possible without passing the target.
  if (hitsWholeTruck(maxBundles + 1)) {
    return quantity.value + (maxBundles + 1) * bundleSize;
  }
  if (maxBundles > 0) {
    return quantity.value + maxBundles * bundleSize;
  }
  return 0;
}
